import { useEffect, useState, useMemo, ChangeEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { signInAnonymously, signOut } from "firebase/auth";
import { httpsCallable, FunctionsError } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage, functions } from "../firebase";
import { EduClassResolution, EduHuntPayload } from "../types";

const RETENTION_DAYS = 30;

export default function StudentHunt() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const name = useMemo(() => sessionStorage.getItem("eduStudentName") || "", []);
  const code = useMemo(() => sessionStorage.getItem("eduClassCode") || "", []);
  const [hunt, setHunt] = useState<EduHuntPayload | null>(null);
  const [submitted, setSubmitted] = useState<Set<number>>(new Set());
  const [uploading, setUploading] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!classId) return;
    // The class code is the student's only credential. It lives beside the
    // name in sessionStorage, so losing one loses both and the join screen
    // asks again.
    if (!name || !code) {
      navigate("/");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // A signed-in REAL account (a teacher demoing on the classroom
        // device, or a browser where the teacher dashboard is logged in)
        // must never be reused for a student flow: the submission would
        // carry the teacher's consumer uid as studentId, outside the
        // anonymous-cleanup ownership model, and the rules now require an
        // anonymous token at create anyway. Sign real accounts out; keep
        // reusing an existing anonymous session.
        if (auth.currentUser && !auth.currentUser.isAnonymous) {
          await signOut(auth);
        }
        if (!auth.currentUser) await signInAnonymously(auth);
        // Students cannot read edu_hunts directly (no rule can prove class
        // membership for an anonymous uid). The resolver returns this
        // class's active hunt and nothing else.
        const resolve = httpsCallable<{ code: string }, EduClassResolution>(
          functions,
          "resolveEduClass"
        );
        const resolved = (await resolve({ code })).data;
        if (cancelled) return;
        if (!resolved.hunt) {
          setError("No active hunt for this class right now. Ask your teacher to start one.");
          setLoading(false);
          return;
        }
        setHunt(resolved.hunt);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          const fnCode = (e as FunctionsError)?.code;
          const msg =
            fnCode === "functions/not-found"
              ? "That class code is no longer valid. Ask your teacher for a new one."
              : e instanceof Error
                ? e.message
                : "Failed to load hunt.";
          setError(msg);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, name, code, navigate]);

  const handleSubmit = async (challengeIndex: number, file: File) => {
    if (!hunt || !auth.currentUser) return;
    // The anonymous uid is this student's only identity. It has always scoped
    // the Storage path; since August 8, 2026 it is also written onto the doc
    // as studentId, so student-scoped work (ownership, a deletion request, a
    // per-student view) is a query instead of string-parsing a photoPath.
    const studentId = auth.currentUser.uid;
    setUploading(challengeIndex);
    setError(null);
    try {
      const path = `edu_submissions/${hunt.huntId}/${studentId}_${Date.now()}.jpg`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type || "image/jpeg" });
      const photoUrl = await getDownloadURL(storageRef);

      const expiresAt = Timestamp.fromMillis(
        Date.now() + RETENTION_DAYS * 24 * 60 * 60 * 1000
      );
      await addDoc(collection(db, "edu_submissions"), {
        huntId: hunt.huntId,
        classId: hunt.classId,
        // Rules enforce studentId == request.auth.uid at create, so this
        // field is trustworthy rather than decorative.
        studentId,
        studentName: name,
        challengeIndex,
        challengeText: hunt.challenges[challengeIndex]?.text || "",
        photoUrl,
        photoPath: path,
        moderationStatus: "pending",
        submittedAt: Timestamp.now(),
        expiresAt,
      });

      setSubmitted((prev) => new Set([...prev, challengeIndex]));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Upload failed.";
      setError(msg);
    } finally {
      setUploading(null);
    }
  };

  const onFile = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleSubmit(i, f);
    e.target.value = "";
  };

  if (loading) {
    return (
      <main className="screen">
        <div className="card"><p className="muted">Loading...</p></div>
      </main>
    );
  }
  if (error && !hunt) {
    return (
      <main className="screen">
        <div className="card">
          <p className="error">{error}</p>
          <button className="link" onClick={() => navigate("/")}>Back to join</button>
        </div>
      </main>
    );
  }
  if (!hunt) return null;

  return (
    <main className="screen">
      <div className="card">
        <h1>{hunt.title}</h1>
        <p className="muted">
          Hi {name}. {submitted.size} of {hunt.challenges.length} submitted.
        </p>
        {error && <p className="error">{error}</p>}
        <ol className="challenges">
          {hunt.challenges.map((c, i) => (
            <li key={i} className={submitted.has(i) ? "done" : ""}>
              <span className="text">{c.text}</span>
              {submitted.has(i) ? (
                <span className="check">Submitted</span>
              ) : (
                <label className="upload-btn">
                  {uploading === i ? "Uploading..." : "Take photo"}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={onFile(i)}
                    disabled={uploading !== null}
                  />
                </label>
              )}
            </li>
          ))}
        </ol>
      </div>
    </main>
  );
}
